import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../utils/format';
import { TrendingUp, TrendingDown, DollarSign, FileText, CheckCircle, Clock, XCircle, Users, Building2, Info } from 'lucide-react';
import { Agency } from '../../types/agency';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DateRange, dateRangesFilter } from '../../utils/period';

interface AgencyStatisticsProps {
  agencies: Agency[];
  startDate: string;
  endDate: string;
  ranges?: DateRange[];
}

interface DetailedStats {
  totalAgencies: number;
  totalNegotiators: number;
  totalSales: number;
  totalPotential: number;
  averageSalePrice: number;
  averageFees: number;
  statusBreakdown: {
    potentiel: number;
    compromis: number;
    acte: number;
    encaisse: number;
    cloture: number;
    annule: number;
  };
  topPerformers: {
    agency: string;
    sales: number;
    count: number;
  }[];
  monthlyTrend: {
    month: string;
    sales: number;
    count: number;
  }[];
  lefeuvreRetrocessions?: {
    transactionCount: number;
    totalRetrocessions: number;
    totalVolume: number;
    averagePercentage: number;
    transactions: {
      propertyNumber: string;
      company: string;
      salePrice: number;
      feesWithoutTax: number;
      retrocessionAmount: number;
      retrocessionPercentage: number;
    }[];
  };
}

// Composant pour les bulles d'information
interface InfoTooltipProps {
  title: string;
  description: string;
  formula?: string;
  example?: string;
}

function InfoTooltip({ title, description, formula, example }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="p-1 text-white/70 hover:text-white transition-colors"
      >
        <Info size={16} />
      </button>
      
      {isVisible && (
        <div className="absolute top-8 right-0 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-[9999]">
          <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-700 mb-3">{description}</p>
          
          {formula && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1">Formule :</p>
              <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-800">
                {formula}
              </div>
            </div>
          )}
          
          {example && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Exemple :</p>
              <p className="text-xs text-gray-700">{example}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgencyStatistics({ agencies, startDate, endDate, ranges = [{ startDate, endDate }] }: AgencyStatisticsProps) {
  const [stats, setStats] = useState<DetailedStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, [agencies, startDate, endDate, JSON.stringify(ranges)]);

  const loadStatistics = async () => {
    try {
      setLoading(true);

      // Récupérer les noms des agences visibles
      const agencyNames = agencies.map(a => a.name);

      if (agencyNames.length === 0) {
        setStats(null);
        setLoading(false);
        return;
      }

      // Vérifier si l'utilisateur a des agences KA
      const hasKAAgencies = agencies.some(a => a.company === 'KA');

      // Charger toutes les transactions pour ces agences
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select(`
          id,
          sale_price,
          fees_without_tax,
          status,
          compromise_date,
          property_number,
          transaction_agencies!inner (
            agency
          ),
          transaction_retrocessions (
            amount,
            type
          )
        `)
        .in('transaction_agencies.agency', agencyNames)
        .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture,annule),or(${dateRangesFilter('compromise_date', ranges)}))`);

      if (error) throw error;

      // Calculer les statistiques
      const statusBreakdown = {
        potentiel: 0,
        compromis: 0,
        acte: 0,
        encaisse: 0,
        cloture: 0,
        annule: 0
      };

      let totalSales = 0;
      let totalPotential = 0;
      let totalSalePrice = 0;
      let totalFees = 0;
      let transactionCount = 0;

      const agencySales = new Map<string, { sales: number; count: number }>();
      const monthlyData = new Map<string, { sales: number; count: number }>();

      // Statistiques Lefeuvre
      const lefeuvreTransactions: any[] = [];
      let lefeuvreRetroTotal = 0;
      let lefeuvreVolumeTotal = 0;

      transactions?.forEach(t => {
        // Calculer les honoraires nets
        const totalRetrocessions = t.transaction_retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
        const netFees = t.fees_without_tax - totalRetrocessions;

        // Compter par statut
        statusBreakdown[t.status as keyof typeof statusBreakdown]++;

        if (t.status === 'potentiel') {
          totalPotential += netFees;
        } else if (t.status !== 'annule') {
          totalSales += netFees;
          totalSalePrice += t.sale_price;
          totalFees += netFees;
          transactionCount++;

          // Statistiques par agence
          t.transaction_agencies.forEach(ta => {
            const current = agencySales.get(ta.agency) || { sales: 0, count: 0 };
            current.sales += netFees;
            current.count++;
            agencySales.set(ta.agency, current);
          });

          // Tendance mensuelle
          if (t.compromise_date) {
            const month = t.compromise_date.substring(0, 7); // YYYY-MM
            const current = monthlyData.get(month) || { sales: 0, count: 0 };
            current.sales += netFees;
            current.count++;
            monthlyData.set(month, current);
          }

          // Rétrocessions Lefeuvre (uniquement pour les agences KA)
          if (hasKAAgencies) {
            const lefeuvreRetro = t.transaction_retrocessions?.find(r => r.type === 'retrocession_lefeuvre');
            if (lefeuvreRetro) {
              const agency = agencies.find(a => t.transaction_agencies.some(ta => ta.agency === a.name));
              if (agency?.company === 'KA') {
                const retrocessionPercentage = (lefeuvreRetro.amount / t.sale_price) * 100;
                lefeuvreTransactions.push({
                  propertyNumber: t.property_number || 'N/A',
                  company: agency.company,
                  salePrice: t.sale_price,
                  feesWithoutTax: t.fees_without_tax,
                  retrocessionAmount: lefeuvreRetro.amount,
                  retrocessionPercentage
                });
                lefeuvreRetroTotal += lefeuvreRetro.amount;
                lefeuvreVolumeTotal += t.sale_price;
              }
            }
          }
        }
      });

      // Top performers
      const topPerformers = Array.from(agencySales.entries())
        .map(([agency, data]) => ({
          agency,
          sales: data.sales,
          count: data.count
        }))
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 5);

      // Tendance mensuelle triée
      const monthlyTrend = Array.from(monthlyData.entries())
        .map(([month, data]) => ({
          month,
          sales: data.sales,
          count: data.count
        }))
        .sort((a, b) => a.month.localeCompare(b.month));

      // Préparer les stats Lefeuvre
      const lefeuvreStats = hasKAAgencies && lefeuvreTransactions.length > 0 ? {
        transactionCount: lefeuvreTransactions.length,
        totalRetrocessions: lefeuvreRetroTotal,
        totalVolume: lefeuvreVolumeTotal,
        averagePercentage: lefeuvreTransactions.length > 0 
          ? lefeuvreTransactions.reduce((sum, t) => sum + t.retrocessionPercentage, 0) / lefeuvreTransactions.length 
          : 0,
        transactions: lefeuvreTransactions
      } : undefined;

      setStats({
        totalAgencies: agencies.length,
        totalNegotiators: agencies.reduce((sum, a) => sum + a.negotiator_count, 0),
        totalSales,
        totalPotential,
        averageSalePrice: transactionCount > 0 ? totalSalePrice / transactionCount : 0,
        averageFees: transactionCount > 0 ? totalFees / transactionCount : 0,
        statusBreakdown,
        topPerformers,
        monthlyTrend,
        lefeuvreRetrocessions: lefeuvreStats
      });
    } catch (err) {
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-4 bg-gray-50 text-gray-700 rounded-lg">
        Aucune donnée disponible
      </div>
    );
  }

  const totalTransactions = Object.values(stats.statusBreakdown).reduce((sum, count) => sum + count, 0);
  const completionRate = agencies.reduce((sum, a) => sum + a.annual_objective, 0) > 0
    ? (stats.totalSales / agencies.reduce((sum, a) => sum + a.annual_objective, 0)) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Graphiques d'évolution mensuelle */}
      {stats.monthlyTrend.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Évolution du CA */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Évolution du CA mensuel</h3>
              <InfoTooltip
                title="Évolution du CA mensuel"
                description="Graphique montrant l'évolution du chiffre d'affaires (honoraires nets) mois par mois."
                formula="SUM(Honoraires HT - Rétrocessions) par mois"
                example="Janvier: 50 000€, Février: 65 000€, Mars: 48 000€..."
              />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.monthlyTrend.map(m => ({
                month: new Date(m.month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
                ca: m.sales
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'CA']}
                  labelStyle={{ color: '#000' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="ca" 
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  dot={{ fill: '#8b5cf6', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Évolution du nombre de ventes */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Nombre de ventes par mois</h3>
              <InfoTooltip
                title="Nombre de ventes par mois"
                description="Graphique montrant le nombre de transactions réalisées chaque mois."
                formula="COUNT(transactions) par mois"
                example="Janvier: 8 ventes, Février: 12 ventes, Mars: 7 ventes..."
              />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.monthlyTrend.map(m => ({
                month: new Date(m.month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
                count: m.count
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [value, 'Ventes']}
                  labelStyle={{ color: '#000' }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#10b981"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Statistiques détaillées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par statut */}
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Répartition par statut</h3>
            <InfoTooltip
              title="Répartition par statut"
              description="Nombre de transactions dans chaque statut. Le potentiel représente les prospects, les autres statuts représentent les ventes réalisées ou en cours."
              formula="COUNT(transactions) GROUP BY statut"
              example="15 potentielles + 8 compromis + 12 actées + 5 encaissées = 40 transactions"
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-700">Potentiel</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900">{stats.statusBreakdown.potentiel}</div>
                <div className="text-sm text-blue-600">{formatCurrency(stats.totalPotential)}</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-gray-700">Compromis</span>
              </div>
              <div className="font-bold text-gray-900">{stats.statusBreakdown.compromis}</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-gray-700">Acte</span>
              </div>
              <div className="font-bold text-gray-900">{stats.statusBreakdown.acte}</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-700">Encaissé</span>
              </div>
              <div className="font-bold text-gray-900">{stats.statusBreakdown.encaisse}</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-gray-700">Clôturé</span>
              </div>
              <div className="font-bold text-gray-900">{stats.statusBreakdown.cloture}</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-gray-700">Annulé</span>
              </div>
              <div className="font-bold text-gray-900">{stats.statusBreakdown.annule}</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-700">Total transactions</span>
              <span className="font-bold text-gray-900">{totalTransactions}</span>
            </div>
          </div>
        </div>

        {/* Moyennes */}
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Moyennes</h3>
            <InfoTooltip
              title="Moyennes"
              description="Calculs des moyennes sur les transactions réalisées (hors potentiel et annulé)."
              formula="SUM(valeurs) / COUNT(transactions réalisées)"
              example="Total CA: 100 000€ / 10 transactions = 10 000€ de CA moyen"
            />
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-gray-600">Prix de vente moyen</p>
                <InfoTooltip
                  title="Prix de vente moyen"
                  description="Moyenne des prix de vente de tous les biens vendus pendant la période."
                  formula="SUM(prix de vente) / COUNT(transactions réalisées)"
                  example="Vente 1: 250 000€ + Vente 2: 180 000€ = 430 000€ / 2 = 215 000€ moyen"
                />
              </div>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(stats.averageSalePrice)}</p>
            </div>

            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-gray-600">Honoraires moyens</p>
                <InfoTooltip
                  title="Honoraires moyens"
                  description="Moyenne des honoraires nets (après rétrocessions) par transaction."
                  formula="SUM(honoraires net HT) / COUNT(transactions réalisées)"
                  example="Transaction 1: 9 829€ + Transaction 2: 7 850€ = 17 679€ / 2 = 8 839,50€ moyen"
                />
              </div>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(stats.averageFees)}</p>
            </div>

            <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-gray-600">CA moyen par transaction</p>
                <InfoTooltip
                  title="CA moyen par transaction"
                  description="Chiffre d'affaires total divisé par le nombre de transactions réalisées."
                  formula="CA Total / Nombre de transactions"
                  example="CA Total: 100 000€ / 10 transactions = 10 000€ par transaction"
                />
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {formatCurrency(stats.averageFees)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tendance mensuelle */}
      {stats.monthlyTrend.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Évolution mensuelle</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Mois</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">CA Réalisé</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Nb Ventes</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">CA Moyen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.monthlyTrend.map((month) => {
                  const date = new Date(month.month + '-01');
                  const monthName = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                  
                  return (
                    <tr key={month.month} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900 capitalize">{monthName}</td>
                      <td className="px-4 py-3 text-right font-bold text-blue-600">
                        {formatCurrency(month.sales)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{month.count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {formatCurrency(month.sales / month.count)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rétrocessions Lefeuvre - Uniquement pour les agences KA */}
      {stats.lefeuvreRetrocessions && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Rétrocessions Lefeuvre</h3>
          </div>

          {/* Cartes statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <p className="text-sm text-gray-600">Transactions Lefeuvre</p>
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {stats.lefeuvreRetrocessions.transactionCount}
              </p>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-5 h-5 text-yellow-600" />
                <p className="text-sm text-gray-600">Total Rétrocessions</p>
              </div>
              <p className="text-2xl font-bold text-yellow-900">
                {formatCurrency(stats.lefeuvreRetrocessions.totalRetrocessions)}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-gray-600">Volume Lefeuvre</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">
                {formatCurrency(stats.lefeuvreRetrocessions.totalVolume)}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-sm text-gray-600">% Rétrocession</p>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {stats.lefeuvreRetrocessions.averagePercentage.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Tableau détaillé */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Détail des transactions avec rétrocessions Lefeuvre
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      N° Bien
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Société
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                      Volume
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                      Honoraires HT
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                      Rétrocession Lefeuvre
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                      % Rétrocession
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.lefeuvreRetrocessions.transactions.map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {transaction.propertyNumber}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {transaction.company}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(transaction.salePrice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(transaction.feesWithoutTax)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-purple-600">
                        {formatCurrency(transaction.retrocessionAmount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                        {transaction.retrocessionPercentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}